import { useTheme } from '../../../../providers/theme';
import { getColors } from '../../../utils/getColors';
import { Button, Col, Row } from 'antd';
import { LambdaChart, LambdaTable, MethodHelp } from './components';
import { useLambdaAnalysis } from './hooks';
import { LAMBDA_METHOD_LABELS } from './methodConstants';
import {
  BROOKS_COREY_EXPONENT_LABEL,
  BROOKS_COREY_PORE_SIZE_INDEX_LABEL,
  toBrooksCoreyPoreSizeDistributionIndex,
} from '../../../../utils/brooksCorey';

type LambdaAnalysisProps = {
  show: boolean;
  showLiao: boolean;
};

export function LambdaAnalysis({ show, showLiao }: LambdaAnalysisProps) {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const {
    chartData,
    isLocalCalculated,
    loading,
    selectedIndexes,
    tableData,
    valuesData,
    numberOfPoints,
    setNumberOfPoints,
    calculateLambda,
    handleCalculateRefinement,
    resetLocalCalculated,
    method,
    changeMethod
  } = useLambdaAnalysis();

  const activeMethod = valuesData?.method ?? method;
  const exponent = valuesData?.lambdaValue;
  const poreSizeIndex = toBrooksCoreyPoreSizeDistributionIndex(exponent);
  const metricLabelStyle = {
    color: colors.textSecondary,
    fontSize: '0.75rem',
    lineHeight: 1.25
  };
  const metricValueStyle = {
    color: colors.text,
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.2,
    fontVariantNumeric: 'tabular-nums'
  };

  if (!show) return null;

  return tableData.length ? (
    <div style={{ marginTop: '1rem' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={24} xl={12}>
          <LambdaTable
            selectedIndexes={selectedIndexes}
            calculateLambda={calculateLambda}
            tableData={tableData}
          />
        </Col>
        <Col xs={24} md={24} xl={12}>
          <MethodHelp
            method={method}
            onChange={changeMethod}
            disabled={loading}
          />
          <div>
            <LambdaChart
              calculateLambda={calculateLambda}
              selectedIndexes={selectedIndexes}
              chartData={chartData}
              showLiao={showLiao}
              numberOfPoints={numberOfPoints}
              setNumberOfPoints={setNumberOfPoints}
            />
          </div>
          <div
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '12px',
              padding: '0.75rem',
              marginTop: '0.5rem',
              marginLeft: '0.5rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(8.5rem, 1fr))',
              gap: '0.75rem 1rem'
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '0.25rem'
              }}
            >
              <span
                style={metricLabelStyle}
                title={`${BROOKS_COREY_EXPONENT_LABEL}. The signed exponent fitted in Pc = Pe · Swn^m; it is negative for a drainage curve.`}
              >
                Fitted exponent, m
              </span>
              <span style={metricValueStyle}>
                {typeof exponent === 'number'
                  ? exponent.toFixed(4)
                  : 'N/A'}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '0.25rem'
              }}
              title="Classic Brooks-Corey pore-size distribution index: λBC = −1 / m. Derived in the browser; it is not persisted."
            >
              <span style={metricLabelStyle}>Pore-size index, λBC</span>
              <span style={metricValueStyle}>
                {poreSizeIndex?.toFixed(4) || 'N/A'}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '0.25rem'
              }}
            >
              <span style={metricLabelStyle}>Entry pressure, Pe</span>
              <span style={metricValueStyle}>
                {valuesData?.pe?.toFixed(4) || 'N/A'}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '0.25rem'
              }}
              title={`Root-mean-square error of the fitted curve vs the lab points (${LAMBDA_METHOD_LABELS[activeMethod]})`}
            >
              <span style={metricLabelStyle}>Fit RMSE</span>
              <span style={metricValueStyle}>
                {typeof valuesData?.fitRmse === 'number'
                  ? valuesData.fitRmse.toFixed(4)
                  : 'N/A'}
              </span>
            </div>
          </div>
          <div
            style={{
              marginTop: '0.5rem',
              marginLeft: '0.5rem',
              display: 'flex',
              gap: '8px'
            }}
          >
            {isLocalCalculated && (
              <Button
                style={{ width: '100%', color: colors.text }}
                onClick={resetLocalCalculated}
                disabled={loading}
              >
                Reset changes
              </Button>
            )}
            <Button
              style={{ width: '100%', color: colors.text }}
              onClick={handleCalculateRefinement}
              loading={loading}
            >
              Save modifications
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  ) : (
    <div className="no-data-div" style={{ marginTop: '1rem' }}>
      No Brooks-Corey curve data computed. Check <strong>Pc Variables</strong> for missing or invalid values and try recalculating.
    </div>
  );
}
